import { DocumentRetrievalExclusionReason } from "@common/types/task";
import { View, ViewDensity } from "@common/types/views/views";
import { ViewTableMeta } from "@design-system/ViewTable/ViewTable.lib";
import { ViewConfig } from "@lib/views";
import { MonthStatus } from "./StatusCellContent";
import { DocVerificationStatus } from "./documentVerificationStatus";
import {
    DocumentRetrievalAccountCell,
    DocumentRetrievalActionsCell,
    DocumentRetrievalFileNameCell,
    DocumentRetrievalPeriodCell,
    DocumentRetrievalRowNumberCell,
    DocumentRetrievalStatementPeriodCell,
    DocumentRetrievalStatusCell,
} from "./Cells/DocumentRetrievalCells";

export enum DocumentRetrievalColumn {
    ROW_NUMBER = "rowNumber",
    PERIOD = "period",
    FILE_NAME = "fileName",
    STATEMENT_PERIOD = "statementPeriod",
    ACCOUNT_NUMBER = "accountNumber",
    STATUS = "status",
    ACTIONS = "actions",
}

export type DocumentRetrievalRowKind = "month" | "unverified" | "separator";

export interface DocumentRetrievalTableRow {
    id: string;
    kind: DocumentRetrievalRowKind;
    rowNumber: number | null;
    periodLabel: string | null;
    fileName: string | null;
    status: MonthStatus | null;
    exclusionReason: DocumentRetrievalExclusionReason | null;
    monthKey: string | null;
    documentId: number | null;
    verification: DocVerificationStatus | null;
    statementPeriod: string | null;
    accountNumber: string | null;
}

export interface DocumentRetrievalTableCallbacks {
    onExclude: (
        monthKey: string,
        reason: DocumentRetrievalExclusionReason,
    ) => void;
    onClearExclusion: (monthKey: string) => void;
    onReview: (documentId: number) => void;
    onRetry: (documentId: number) => void;
    onUpload: () => void;
    onRemoveFromTask: (documentId: number) => void;
    isRemovingDocument: (documentId: number) => boolean;
}

export type DocumentRetrievalTableMeta = ViewTableMeta<
    DocumentRetrievalTableCallbacks,
    never
>;

export const documentRetrievalViewConfig: ViewConfig<
    DocumentRetrievalTableRow,
    DocumentRetrievalColumn,
    never
> = {
    columns: {
        [DocumentRetrievalColumn.ROW_NUMBER]: {
            accessorKey: "rowNumber",
            name: "#",
            header: "#",
            immutable: true,
            hiddenFromSettings: true,
            minSize: 40,
            maxSize: 40,
            cell: DocumentRetrievalRowNumberCell,
        },
        [DocumentRetrievalColumn.PERIOD]: {
            accessorKey: "periodLabel",
            name: "Period",
            header: "Period",
            immutable: true,
            minSize: 140,
            maxSize: 160,
            cell: DocumentRetrievalPeriodCell,
        },
        [DocumentRetrievalColumn.FILE_NAME]: {
            accessorKey: "fileName",
            name: "File name",
            header: "File name",
            immutable: true,
            minSize: 200,
            cell: DocumentRetrievalFileNameCell,
        },
        [DocumentRetrievalColumn.STATEMENT_PERIOD]: {
            accessorKey: "statementPeriod",
            name: "Statement period",
            header: "Statement period",
            minSize: 180,
            maxSize: 240,
            cell: DocumentRetrievalStatementPeriodCell,
        },
        [DocumentRetrievalColumn.ACCOUNT_NUMBER]: {
            accessorKey: "accountNumber",
            name: "Account",
            header: "Account",
            minSize: 120,
            maxSize: 160,
            cell: DocumentRetrievalAccountCell,
        },
        [DocumentRetrievalColumn.STATUS]: {
            accessorKey: "status",
            name: "Status",
            header: "Status",
            immutable: true,
            minSize: 210,
            maxSize: 240,
            cell: DocumentRetrievalStatusCell,
        },
        [DocumentRetrievalColumn.ACTIONS]: {
            accessorKey: "kind",
            name: "",
            header: "",
            immutable: true,
            hiddenFromSettings: true,
            minSize: 148,
            maxSize: 148,
            cell: DocumentRetrievalActionsCell,
        },
    },
};

export const documentRetrievalDefaultView: Pick<
    View,
    "filters" | "columns" | "options"
> = {
    filters: {},
    columns: [
        { id: DocumentRetrievalColumn.ROW_NUMBER },
        { id: DocumentRetrievalColumn.PERIOD },
        { id: DocumentRetrievalColumn.FILE_NAME },
        { id: DocumentRetrievalColumn.STATEMENT_PERIOD },
        { id: DocumentRetrievalColumn.ACCOUNT_NUMBER },
        { id: DocumentRetrievalColumn.STATUS },
        {
            id: DocumentRetrievalColumn.ACTIONS,
            options: { pin: "right" },
        },
    ],
    options: {
        density: ViewDensity.MD,
        bulkActions: true,
        selection: true,
    },
};
